import { SelectOptions } from 'components/commonType';
import i18n from 'i18n';
import { uniq } from 'lodash';

/**
 * 商品分類的路徑分隔字元。
 *
 * 分類樹的每個節點 value 都是一條逐層累加的 category id 路徑，例如
 * `122000000000000 > 122010000000000 > 122010003000000 > 122010003001000`。
 * 父層路徑必然是子層路徑的字串前綴，層級關係一律由這個規則推導，
 * 不從 id 本身的數字結構判斷。
 */
export const CATEGORY_PATH_SEPARATOR = ' > ';

const CATEGORY_I18N_PREFIX = 'productCategories';

export function splitCategoryPath (path: string): string[] {
  return path.split('>').map(segment => segment.trim());
}

/**
 * 判斷一條分類路徑是否屬於某個 L1（Inventory 分頁）。
 * 比對時帶上分隔字元，避免某個 L1 id 是另一個 L1 id 的前綴時誤判。
 */
export function belongsToInventory (path: string, inventoryName: string): boolean {
  return (
    path === inventoryName ||
    path.startsWith(`${inventoryName}${CATEGORY_PATH_SEPARATOR}`)
  );
}

/**
 * 把 category id 路徑轉成顯示用的名稱路徑（含上層路徑），例如
 * `保健食品/用品 > 保健用品`。
 *
 * 只有「完整葉節點路徑」與「單一 id 片段」兩種 i18n key 會被註冊
 * （見 useProductCategorySegmentFields 的 generateI18nOfSelectOptions），
 * 中間層的完整路徑沒有對應的 key，所以這裡逐段翻譯後再接回去。
 * 翻不到的片段回退成該片段的原始 id。
 */
export function pathToLabel (path: string): string {
  return splitCategoryPath(path)
    .map(segment => {
      const key = `${CATEGORY_I18N_PREFIX}.${segment}`;
      return i18n.exists(key) ? i18n.t<string>(key) : segment;
    })
    .join(CATEGORY_PATH_SEPARATOR);
}

// options 為 undefined 或空陣列都算葉節點：分類樹上確實存在沒有下層的 L2。
const isLeafOption = (option: SelectOptions): boolean =>
  !option.options || option.options.length === 0;

/**
 * 把已存的分類清單盡量反推回父層呈現：某個節點底下的葉節點全部被選中時，
 * 收合成該節點一筆。反推不出來的原樣保留，不丟棄也不強制轉換。
 *
 * `optionsByInventory` 是每個 L1 分頁各自的 option 陣列。這些陣列的 root
 * 就是 L2 節點（L1 本身不是樹上的節點，而是左側欄的分頁），
 * 因此不需要額外的層級保護就不會收合到 L1。
 */
export function collapseSelectedPaths (
  selected: string[],
  optionsByInventory: SelectOptions[][]
): string[] {
  if (selected.length === 0) {
    return [];
  }

  const selectedSet = new Set(selected);
  const collapsed: string[] = [];

  // 這棵子樹是否「整個被涵蓋」。唯一回傳 false 的來源是「沒被選中的葉節點」。
  const isFullySelected = (option: SelectOptions): boolean => {
    // 明確選了這個節點，就代表底下整棵都算，不必再往下看。
    if (selectedSet.has(option.value.toString())) {
      return true;
    }
    if (isLeafOption(option)) {
      return false;
    }
    return (option.options as SelectOptions[]).every(isFullySelected);
  };

  // pre-order DFS，由上往下找「最高的可收合節點」，找到就不再下探，
  // 因此輸出不會同時出現父層與其後代。
  const collect = (option: SelectOptions) => {
    const path = option.value.toString();
    // 節點本身就在清單裡：沿用這個層級，底下的子項一律不再輸出。
    // 與下方 isFullySelected 的判斷重疊，但可省下整棵子樹的走訪。
    if (selectedSet.has(path)) {
      collapsed.push(path);
      return;
    }
    if (isLeafOption(option)) {
      return;
    }
    // 底下的葉節點全被選中 → 收合成這一筆。
    if (isFullySelected(option)) {
      collapsed.push(path);
      return;
    }
    // 只有部分被選，往下找更細的層級。
    (option.options as SelectOptions[]).forEach(collect);
  };

  optionsByInventory.forEach(options => options.forEach(collect));

  // path 就是 collapsed 的某一筆，或是已被收合掉的後代。
  const isCoveredByCollapsed = (path: string) =>
    collapsed.some(
      collapsedPath =>
        path === collapsedPath ||
        path.startsWith(`${collapsedPath}${CATEGORY_PATH_SEPARATOR}`)
    );

  // collapsed 只涵蓋「在樹上找得到」的路徑。樹上已不存在的舊項目（分類下架等）
  // 反推不出來，就從原始清單原樣接回去，不靜默丟掉。
  return uniq([
    ...collapsed,
    ...selected.filter(path => !isCoveredByCollapsed(path))
  ]);
}

/**
 * 算出某個 L1 分頁的選取變動之後、整個欄位的新 value。
 *
 * 只替換「屬於該 L1 且在分類樹上找得到」的那一段，因此：
 * - 其他 L1 分頁的已選項目不受影響。
 * - 已存但目前樹上找不到的舊項目（分類下架等）不會被這次操作靜默刪掉。
 *
 * 不能改成「以 L1 前綴一刀切」，那會讓上述第二種項目在使用者點任何東西時消失。
 */
export function nextValueAfterSelect (
  currentValue: string[],
  resolvedPathsOfInventory: string[],
  nextPathsOfInventory: string[]
): string[] {
  const resolved = new Set(resolvedPathsOfInventory);
  const next = new Set(nextPathsOfInventory);

  // 只移除「在這次操作範圍內（resolved）且已被取消（不在 next）」的項目。
  // 其他 L1 的項目與樹上找不到的孤兒都不在 resolved 裡，一律保留。
  // 留下來的維持原本順序，避免 tag 區在每次點選後整排重排。
  const result = currentValue.filter(
    path => !resolved.has(path) || next.has(path)
  );
  // 新增的接在尾端；seen 用來擋掉上面已經保留下來的同一筆。
  const seen = new Set(result);
  nextPathsOfInventory.forEach(path => {
    if (!seen.has(path)) {
      seen.add(path);
      result.push(path);
    }
  });

  return result;
}
